import {action, makeAutoObservable, observable} from "mobx";
import {ViewController} from "data/types/structure";
import {inject, injectable} from "inversify";
import type {ILocalizationStore} from "data/stores/localization/localization.store";
import {Bindings} from "data/constants/bindings";
import {RequestState} from "data/enums";
import {AxiosError} from "axios";
import {IApiResponse} from "data/services/http";
import {chain, isEqual} from "lodash";
import type {IRoundsStore} from "data/stores/rounds/rounds.store";
import type {ISquad, ISquadsStore} from "data/stores/squads/squads.store";
import type {IPlayer, IPlayersStore} from "data/stores/players/players.store";
import type {IFantasyStore} from "data/stores/fantasy/fantasy.store";
import {getLuminance} from "@mui/material";

interface IParam {
	clubID?: string;
}

export interface IFantasyController extends ViewController<IParam> {
	readonly i18n: ILocalizationStore;
	get club(): ISquad | undefined;
	get badge(): string | undefined;
	get players(): IPlayer[];
	get selectedPlayerId(): number | null;
	get isLoaded(): boolean;
}

@injectable()
export class FantasyController implements IFantasyController {
	@observable private _requestState: RequestState = RequestState.IDLE;
	@observable private _clubID: string | undefined;

	get club() {
		if (!this._clubID) {
			return;
		}
		return this._squadsStore.getSquadByAbbr(this._clubID);
	}

	get badge() {
		if (!this.club) {
			return;
		}
		const luminance = getLuminance(this.club.backgroundColor);
		return luminance > 0.5 ? this.club.darkBadge : this.club.lightBadge;
	}

	get players() {
		const stat = this._roundsStore.isSeasonStarted ? "totalPoints" : "percentSelected";
		return chain(this._playersStore.list)
			.filter((player) => (this.club ? player.squadId === this.club.id : true))
			.orderBy(stat, "desc")
			.slice(0, 2)
			.value();
	}

	get selectedPlayerId() {
		return this._fantasyStore.selectedPlayerId;
	}

	get isLoaded() {
		return isEqual(this._requestState, RequestState.SUCCESS);
	}

	constructor(
		@inject(Bindings.LocalizationStore) readonly i18n: ILocalizationStore,
		@inject(Bindings.RoundsStore) private _roundsStore: IRoundsStore,
		@inject(Bindings.SquadsStore) private _squadsStore: ISquadsStore,
		@inject(Bindings.PlayersStore) private _playersStore: IPlayersStore,
		@inject(Bindings.FantasyStore) private _fantasyStore: IFantasyStore
	) {
		makeAutoObservable(this);
	}

	@action private onError = (error: AxiosError<IApiResponse>) => {
		this._requestState = RequestState.ERROR;
	};

	@action private onSuccess = () => {
		this._requestState = RequestState.SUCCESS;
	};

	@action init({clubID}: IParam): void {
		this._clubID = clubID;

		this._requestState = RequestState.PENDING;

		void Promise.all([
			this._roundsStore.fetchRounds(),
			this._squadsStore.fetchSquads(),
			this._playersStore.fetchPlayers(),
		])
			.then(this.onSuccess)
			.catch(this.onError);
	}
}
